import { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "sylviq-turnstile-api";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileOptions = {
  action: string;
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  sitekey: string;
  theme: "dark" | "light" | "auto";
};

type TurnstileApi = {
  remove: (widgetId: string) => void;
  render: (container: HTMLElement, options: TurnstileOptions) => string;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    const ready = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile loaded without its API"));
    };
    script.addEventListener("load", ready, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile failed to load")),
      {
        once: true,
      },
    );
    if (!existing) {
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = SCRIPT_SRC;
      document.head.appendChild(script);
    }
  });
  scriptPromise = pending.catch((error: unknown) => {
    // A blocked or failed script must be retryable by the person instead of
    // leaving a permanently rejected promise or a dead script element behind.
    scriptPromise = undefined;
    document.getElementById(SCRIPT_ID)?.remove();
    throw error;
  });
  return scriptPromise;
}

export function TurnstileChallenge({
  action,
  onToken,
  siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY,
}: {
  action: string;
  onToken: (token: string | null) => void;
  siteKey?: string;
}) {
  const container = useRef<HTMLFieldSetElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !container.current) return;
    let removed = false;
    let widgetId: string | undefined;
    setFailed(false);
    void loadTurnstile()
      .then((turnstile) => {
        if (removed || !container.current) return;
        widgetId = turnstile.render(container.current, {
          sitekey: siteKey,
          action,
          theme: "dark",
          callback: (token) => {
            setFailed(false);
            onToken(token);
          },
          "error-callback": () => {
            setFailed(true);
            onToken(null);
          },
          "expired-callback": () => onToken(null),
        });
      })
      .catch(() => {
        setFailed(true);
        onToken(null);
      });
    return () => {
      removed = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, attempt, onToken, siteKey]);

  if (!siteKey) return null;
  return (
    <div className="grid gap-2">
      <fieldset
        ref={container}
        className="m-0 min-w-0 border-0 p-0"
        aria-label="Verification challenge"
      />
      {failed ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--danger)]" role="alert">
          <span>Verification could not load. Check your connection and try again.</span>
          <button
            type="button"
            className="font-medium underline underline-offset-4"
            onClick={() => {
              onToken(null);
              setAttempt((value) => value + 1);
            }}
          >
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}
